/**
 * Weekly and monthly activity cards (docs/design/social-plan.md 3, "Activity") -- pure rules, no
 * I/O. A card is built on the device from the person's own notes and holds enumerated lexicon
 * codes and numbers only, never note text; the edge validates the same shape with the same rules.
 * Periods are UTC: the ISO week (Monday to Sunday) and the calendar month.
 */
import {
  ACHIEVEMENT_RULES,
  evaluateAchievements,
  isoWeekKey,
  streakFromWeeks,
  type TastingSummary,
} from "../gamification/achievements.js";
import { isCountryCode } from "../lexicon/countries.js";
import { foldName } from "../lexicon/fold.js";
import { grapeCodeOf, isGrapeCode } from "../lexicon/grapes.js";
import { isRegionCode, regionCodeOf } from "../lexicon/regions.js";
import type { Sweetness, WineColour } from "../scales.js";

export const CARD_PERIODS = ["week", "month"] as const;
export type CardPeriod = (typeof CARD_PERIODS)[number];

export const WINE_TYPES = ["red", "white", "rose", "sparkling", "fortified", "sweet"] as const;
export type WineType = (typeof WINE_TYPES)[number];

/** A small fixed set, no comments: reacting should never need moderating. */
export const REACTION_KINDS = ["good-call", "cheers", "wow", "jealous"] as const;
export type ReactionKind = (typeof REACTION_KINDS)[number];

/** The version of the card payload, so a later shape can tell old rows from new ones. */
export const CARD_SCHEMA = 1;
/** Cards are kept a year; their reactions go with them. */
export const CARD_RETENTION_DAYS = 365;
/** Most groups one card can be shown to. */
export const CARD_MAX_GROUPS = 20;
/** Entries a card lists per kind: the most-tasted first, the rest are only in the counts. */
export const CARD_LIST_LIMITS = { regions: 12, countries: 10, grapes: 12 } as const;

export interface CardTally {
  readonly code: string;
  readonly count: number;
  /** Not in the person's history before the period began. */
  readonly isNew: boolean;
}

export interface CardContent {
  readonly schema: typeof CARD_SCHEMA;
  readonly counts: { readonly wines: number; readonly notes: number; readonly sessions: number };
  readonly wineTypes: readonly { readonly type: WineType; readonly count: number }[];
  readonly regions: readonly CardTally[];
  readonly countries: readonly CardTally[];
  readonly grapes: readonly CardTally[];
  readonly blind: { readonly calls: number; readonly correct: number };
  /** Consecutive ISO weeks with a note, counted back from the end of the period. */
  readonly streakWeeks: number;
  /** Ids of the achievements first satisfied during the period. */
  readonly badges: readonly string[];
}

// ---- periods ----

const DAY_MS = 86_400_000;
const WEEK_KEY = /^(\d{4})-W(\d{2})$/;
const MONTH_KEY = /^(\d{4})-(\d{2})$/;

function pad(value: number, width = 2): string {
  return String(value).padStart(width, "0");
}

/** The key of the period an instant falls in: `2026-W42` or `2026-10`. */
export function periodKeyOf(period: CardPeriod, at: Date): string {
  return period === "week"
    ? isoWeekKey(at)
    : `${pad(at.getUTCFullYear(), 4)}-${pad(at.getUTCMonth() + 1)}`;
}

/** The half-open range a period covers, or undefined when the key is not a real period. */
export function periodRange(
  period: CardPeriod,
  key: string,
): { readonly start: Date; readonly end: Date } | undefined {
  if (period === "month") {
    const match = MONTH_KEY.exec(key);
    const year = Number(match?.[1]);
    const month = Number(match?.[2]);
    if (match === null || month < 1 || month > 12) return undefined;
    return {
      start: new Date(Date.UTC(year, month - 1, 1)),
      end: new Date(Date.UTC(year, month, 1)),
    };
  }
  const match = WEEK_KEY.exec(key);
  if (match === null) return undefined;
  const year = Number(match[1]);
  const week = Number(match[2]);
  // Week 1 holds 4 January; its Monday is the anchor every other week is counted from.
  const jan4 = new Date(Date.UTC(year, 0, 4));
  const monday = jan4.getTime() - ((jan4.getUTCDay() + 6) % 7) * DAY_MS;
  const start = new Date(monday + (week - 1) * 7 * DAY_MS);
  // A year has 52 or 53 weeks; a week number past that rolls into the next year's key.
  if (week < 1 || isoWeekKey(start) !== key) return undefined;
  return { start, end: new Date(start.getTime() + 7 * DAY_MS) };
}

export function isPeriodKey(period: CardPeriod, key: string): boolean {
  return periodRange(period, key) !== undefined;
}

/** The key of the period before the one `now` is in: the last one that is over. */
export function previousPeriodKey(period: CardPeriod, now: Date): string {
  const range = periodRange(period, periodKeyOf(period, now));
  if (range === undefined) throw new RangeError("a period key of an instant is always valid");
  return periodKeyOf(period, new Date(range.start.getTime() - 1));
}

// ---- what a note says about the wine ----

/** What the aggregation reads of one note and its wine; the PWA adapts a saved tasting to it. */
export interface CardTasting {
  /** ISO 8601 instant. */
  readonly tastedAt: string;
  /** The wine's id; null for a blind wine not yet shown, which counts as a wine of its own. */
  readonly wineKey: string | null;
  /** Null while the wine is unknown. */
  readonly colour: WineColour | null;
  readonly sparkling: boolean;
  /** The sweetness the taster wrote down. */
  readonly sweetness: Sweetness | null;
  readonly country?: string | undefined;
  readonly region?: string | undefined;
  readonly appellation?: string | undefined;
  readonly grapes: readonly string[];
  readonly blind: boolean;
  /** The taster named a grape or a country before the reveal. */
  readonly blindGuessed: boolean;
  readonly blindCorrect: boolean | null;
}

/** Fortified is a style no colour field holds; these names, in these countries, say it. */
const FORTIFIED: Readonly<Record<string, RegExp>> = {
  PT: /\b(porto?|madeira)\b/,
  ES: /\b(sherry|jerez|xeres|manzanilla)\b/,
  IT: /\bmarsala\b/,
  FR: /\b(banyuls|maury|rivesaltes)\b/,
};

/** The type a note counts as: sparkling, then fortified, then sweet, then its colour. */
export function wineTypeOf(tasting: CardTasting): WineType | undefined {
  if (tasting.colour === null) return undefined;
  if (tasting.sparkling) return "sparkling";
  const country = tasting.country?.toUpperCase() ?? "";
  const named = foldName(`${tasting.appellation ?? ""} ${tasting.region ?? ""}`);
  if (Object.hasOwn(FORTIFIED, country) && FORTIFIED[country]?.test(named) === true) {
    return "fortified";
  }
  if (tasting.sweetness === "sweet" || tasting.sweetness === "luscious") return "sweet";
  return tasting.colour;
}

function regionOf(tasting: CardTasting): string | undefined {
  const country = tasting.country;
  return (
    (tasting.region === undefined ? undefined : regionCodeOf(tasting.region, country)) ??
    (tasting.appellation === undefined ? undefined : regionCodeOf(tasting.appellation, country))
  );
}

function countryOf(tasting: CardTasting): string | undefined {
  const code = tasting.country?.toUpperCase();
  return code !== undefined && isCountryCode(code) ? code : undefined;
}

function grapesOf(tasting: CardTasting): string[] {
  const codes = tasting.grapes.flatMap((name) => grapeCodeOf(name) ?? []);
  return [...new Set(codes)];
}

/** The codes of one note, per kind; a note counts once for each code it holds. */
function codesOf(tasting: CardTasting): {
  regions: string[];
  countries: string[];
  grapes: string[];
} {
  const region = regionOf(tasting);
  const country = countryOf(tasting);
  return {
    regions: region === undefined ? [] : [region],
    countries: country === undefined ? [] : [country],
    grapes: grapesOf(tasting),
  };
}

// ---- building ----

const at = (tasting: CardTasting): number => Date.parse(tasting.tastedAt);

function summaryOf(tasting: CardTasting): TastingSummary {
  const grape = tasting.grapes[0];
  return {
    tastedAt: tasting.tastedAt,
    ...(grape === undefined ? {} : { grape }),
    ...(tasting.country === undefined ? {} : { country: tasting.country }),
    ...(tasting.region === undefined ? {} : { region: tasting.region }),
    blind: tasting.blind,
    ...(tasting.blindCorrect === null ? {} : { blindCorrect: tasting.blindCorrect }),
  };
}

function ranked(counts: ReadonlyMap<string, number>, known: ReadonlySet<string>, limit: number) {
  return [...counts]
    .toSorted(([a, x], [b, y]) => y - x || a.localeCompare(b))
    .slice(0, limit)
    .map(([code, count]): CardTally => ({ code, count, isNew: !known.has(code) }));
}

export interface BuildCardInput {
  readonly period: CardPeriod;
  readonly key: string;
  /** Every note the person has, not only the period's: "new" and the streak need the history. */
  readonly tastings: readonly CardTasting[];
  /** Sessions the person took part in during the period. */
  readonly sessionsJoined: number;
}

/** The card for one period, or undefined when `key` is not a period. */
export function buildCardContent(input: BuildCardInput): CardContent | undefined {
  const range = periodRange(input.period, input.key);
  if (range === undefined) return undefined;
  const start = range.start.getTime();
  const end = range.end.getTime();
  const inPeriod = input.tastings.filter((t) => at(t) >= start && at(t) < end);
  const before = input.tastings.filter((t) => at(t) < start);
  const upToEnd = input.tastings.filter((t) => at(t) < end);

  const seen = {
    regions: new Set<string>(),
    countries: new Set<string>(),
    grapes: new Set<string>(),
  };
  for (const tasting of before) {
    const codes = codesOf(tasting);
    for (const code of codes.regions) seen.regions.add(code);
    for (const code of codes.countries) seen.countries.add(code);
    for (const code of codes.grapes) seen.grapes.add(code);
  }

  const tally = {
    regions: new Map<string, number>(),
    countries: new Map<string, number>(),
    grapes: new Map<string, number>(),
  };
  const types = new Map<WineType, number>();
  const wines = new Set<string>();
  let unshown = 0;
  let calls = 0;
  let correct = 0;
  for (const tasting of inPeriod) {
    if (tasting.wineKey === null) unshown += 1;
    else wines.add(tasting.wineKey);
    const type = wineTypeOf(tasting);
    if (type !== undefined) types.set(type, (types.get(type) ?? 0) + 1);
    const codes = codesOf(tasting);
    for (const kind of ["regions", "countries", "grapes"] as const) {
      for (const code of codes[kind]) tally[kind].set(code, (tally[kind].get(code) ?? 0) + 1);
    }
    if (tasting.blind && tasting.blindGuessed) calls += 1;
    if (tasting.blind && tasting.blindCorrect === true) correct += 1;
  }

  const summaries = upToEnd.map(summaryOf);
  const unlocked = new Set(
    ACHIEVEMENT_RULES.filter((rule) => rule.check(before.map(summaryOf))).map((rule) => rule.id),
  );
  const weeks = new Set(upToEnd.map((t) => isoWeekKey(new Date(at(t)))));

  return {
    schema: CARD_SCHEMA,
    counts: { wines: wines.size + unshown, notes: inPeriod.length, sessions: input.sessionsJoined },
    wineTypes: [...types]
      .toSorted(([a, x], [b, y]) => y - x || a.localeCompare(b))
      .map(([type, count]) => ({ type, count })),
    regions: ranked(tally.regions, seen.regions, CARD_LIST_LIMITS.regions),
    countries: ranked(tally.countries, seen.countries, CARD_LIST_LIMITS.countries),
    grapes: ranked(tally.grapes, seen.grapes, CARD_LIST_LIMITS.grapes),
    blind: { calls, correct },
    streakWeeks: streakFromWeeks(weeks, new Date(end - 1)),
    badges: evaluateAchievements(summaries, unlocked).map((rule) => rule.id),
  };
}

// ---- validation, shared by the edge and the tests ----

const BADGE_IDS: ReadonlySet<string> = new Set(ACHIEVEMENT_RULES.map((rule) => rule.id));
/** Far more than a person can taste in a month, so an inflated card is plainly wrong. */
const MAX_COUNT = 100_000;

function tallyIssues(
  name: string,
  tallies: readonly CardTally[],
  known: (code: string) => boolean,
  limit: number,
  notes: number,
): string[] {
  const issues: string[] = [];
  if (tallies.length > limit) issues.push(`${name}: at most ${String(limit)} entries`);
  const codes = tallies.map((t) => t.code);
  if (new Set(codes).size !== codes.length) issues.push(`${name}: a code is listed twice`);
  for (const entry of tallies) {
    if (!known(entry.code)) issues.push(`${name}: unknown code ${entry.code}`);
    if (!Number.isInteger(entry.count) || entry.count < 1 || entry.count > notes) {
      issues.push(`${name}: a count outside 1..notes`);
    }
  }
  return issues;
}

/** What is wrong with a card's numbers, or an empty list when it adds up. */
export function cardContentIssues(card: CardContent): string[] {
  const { wines, notes, sessions } = card.counts;
  const issues: string[] = [];
  for (const value of [
    wines,
    notes,
    sessions,
    card.blind.calls,
    card.blind.correct,
    card.streakWeeks,
  ]) {
    if (!Number.isInteger(value) || value < 0 || value > MAX_COUNT) {
      issues.push("a count is not a whole number in range");
    }
  }
  if (notes + sessions < 1) issues.push("a card needs a note or a session");
  if (wines > notes) issues.push("more wines than notes");
  if (card.blind.calls > notes) issues.push("more blind calls than notes");
  if (card.blind.correct > card.blind.calls) issues.push("more correct calls than calls");

  const typeNames = card.wineTypes.map((t) => t.type);
  if (new Set(typeNames).size !== typeNames.length) issues.push("a wine type is listed twice");
  if (card.wineTypes.some((t) => !Number.isInteger(t.count) || t.count < 1)) {
    issues.push("a wine type count below 1");
  }
  if (card.wineTypes.reduce((sum, t) => sum + t.count, 0) > notes) {
    issues.push("wine types add up to more than the notes");
  }

  issues.push(
    ...tallyIssues("regions", card.regions, isRegionCode, CARD_LIST_LIMITS.regions, notes),
    ...tallyIssues("countries", card.countries, isCountryCode, CARD_LIST_LIMITS.countries, notes),
    ...tallyIssues("grapes", card.grapes, isGrapeCode, CARD_LIST_LIMITS.grapes, notes),
  );

  if (new Set(card.badges).size !== card.badges.length) issues.push("a badge is listed twice");
  if (card.badges.some((id) => !BADGE_IDS.has(id))) issues.push("an unknown badge");
  return issues;
}

// ---- who sees a card ----

export interface CardAudience {
  /** Accepted friends. */
  readonly friends: boolean;
  /** Groups whose active members see it; the person has to be one of them too. */
  readonly groupIds: readonly string[];
}

/** What is wrong with the audience of a card, or an empty list when someone is meant to see it. */
export function audienceIssues(audience: CardAudience): string[] {
  const issues: string[] = [];
  if (!audience.friends && audience.groupIds.length === 0) issues.push("pick who sees the card");
  if (audience.groupIds.length > CARD_MAX_GROUPS) {
    issues.push(`at most ${String(CARD_MAX_GROUPS)} groups`);
  }
  if (new Set(audience.groupIds).size !== audience.groupIds.length) {
    issues.push("a group is listed twice");
  }
  return issues;
}
